import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { templatesApi } from "@/api";
import type { Template } from "@/types";
import { countSmsChars, SMS_MAX_CHARS, SMS_PREFIX } from "@/utils/sms";
import HelpTip from "@/components/ui/HelpTip";

export default function Templates() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Partial<Template> | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["templates"],
    queryFn: () => templatesApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: (t: { slug: string; body: string; category: string; active: boolean }) =>
      templatesApi.create(t as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      setEditing(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<Template>) =>
      templatesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      setEditing(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => templatesApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["templates"] }),
  });

  const charInfo = editing?.body ? countSmsChars(editing.body) : null;

  const handleSave = () => {
    if (!editing?.slug || !editing?.body || !editing?.category) return;
    if (charInfo?.overLimit) return;
    if (editing.id) {
      updateMutation.mutate(editing as any);
    } else {
      createMutation.mutate({
        slug: editing.slug,
        body: editing.body,
        category: editing.category,
        active: editing.active ?? true,
      });
    }
  };

  // {{variable}} を React 要素としてハイライト（XSS 防止）
  const highlightVars = (body: string) => {
    const parts = body.split(/(\{\{\w+\}\})/g);
    return parts.map((part, i) =>
      /^\{\{\w+\}\}$/.test(part) ? (
        <span key={i} className="bg-blue-100 text-blue-700 px-1 rounded">
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">テンプレート</h2>
        <button
          onClick={() =>
            setEditing({ slug: "", body: "", category: "transactional", active: true })
          }
          className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600"
        >
          新規作成
        </button>
      </div>

      {isLoading ? (
        <p className="text-slate-500">読み込み中...</p>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Slug</th>
                <th className="px-4 py-3 text-left font-medium">カテゴリ</th>
                <th className="px-4 py-3 text-left font-medium">本文<HelpTip text="通数バッジの色: 灰=1通、黄=2通、赤=3通以上。通数が多いほどコスト増" /></th>
                <th className="px-4 py-3 text-left font-medium">有効</th>
                <th className="px-4 py-3 text-left font-medium">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data?.data.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono">{t.slug}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${
                        t.category === "transactional"
                          ? "bg-green-100 text-green-800"
                          : "bg-orange-100 text-orange-800"
                      }`}
                    >
                      {t.category}
                    </span>
                  </td>
                  <td className="px-4 py-3 max-w-sm">
                    <p className="truncate">{t.body}</p>
                    {(() => {
                      const info = countSmsChars(t.body);
                      return (
                        <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs ${
                          info.segments >= 3 ? "bg-red-100 text-red-700" :
                          info.segments >= 2 ? "bg-amber-100 text-amber-700" :
                          "bg-slate-100 text-slate-500"
                        }`}>
                          {info.segments}通 / {(info.segments * 3.3).toFixed(1)}円
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3">{t.active ? "ON" : "OFF"}</td>
                  <td className="px-4 py-3 space-x-2">
                    <button
                      onClick={() => setEditing(t)}
                      className="text-blue-600 hover:underline"
                    >
                      編集
                    </button>
                    <button
                      onClick={() => {
                        if (confirm("削除しますか？")) deleteMutation.mutate(t.id);
                      }}
                      className="text-red-600 hover:underline"
                    >
                      削除
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 編集ダイアログ */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[500px] space-y-4">
            <h3 className="font-bold">
              {editing.id ? "テンプレート編集" : "テンプレート作成"}
            </h3>
            <input
              type="text"
              placeholder="Slug (例: order_confirm)"
              value={editing.slug || ""}
              onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
              className="w-full px-3 py-2 border rounded-md text-sm"
            />
            <select
              value={editing.category || "transactional"}
              onChange={(e) => setEditing({ ...editing, category: e.target.value as any })}
              className="w-full px-3 py-2 border rounded-md text-sm"
            >
              <option value="transactional">Transactional</option>
              <option value="marketing">Marketing</option>
            </select>
            <textarea
              placeholder="本文 ({{name}} で変数挿入)"
              value={editing.body || ""}
              onChange={(e) => setEditing({ ...editing, body: e.target.value })}
              rows={4}
              className={`w-full px-3 py-2 border rounded-md text-sm ${
                charInfo?.overLimit ? "border-red-400" : ""
              }`}
            />
            {charInfo && charInfo.rawChars > 0 && (
              <>
                <div className={`flex justify-between text-xs ${
                  charInfo.overLimit ? "text-red-600" : charInfo.remaining < 30 ? "text-orange-600" : "text-slate-500"
                }`}>
                  <span>
                    展開見込み: 約{charInfo.estimatedChars} / {SMS_MAX_CHARS}文字
                    {charInfo.segments > 0 && `（${charInfo.segments}通分 / ${(charInfo.segments * 3.3).toFixed(1)}円）`}
                  </span>
                  <span>
                    {charInfo.overLimit
                      ? `${-charInfo.remaining}文字超過`
                      : `残り約${charInfo.remaining}文字`}
                  </span>
                </div>
                <p className="text-xs text-slate-400">
                  {SMS_PREFIX} ({SMS_PREFIX.length}文字) が自動付与されます。70文字で1通、以降67文字ごとに+1通課金。
                </p>
                {charInfo.segments >= 2 && (
                  <p className="text-xs text-amber-600 mt-1">
                    このテンプレートは{charInfo.segments}通分（{(charInfo.segments * 3.3).toFixed(1)}円/件）です。
                  </p>
                )}
              </>
            )}
            {charInfo?.overLimit && (
              <p className="text-xs text-red-600">
                展開見込みが{SMS_MAX_CHARS}文字を超えています。本文を短くしてください。
              </p>
            )}
            {editing.body && (
              <div className="p-3 bg-slate-50 rounded-md text-sm">
                <p className="text-xs text-slate-500 mb-1">プレビュー:</p>
                <p>{highlightVars(editing.body)}</p>
              </div>
            )}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={editing.active ?? true}
                onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
              />
              有効
            </label>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2 border rounded-md text-sm"
              >
                キャンセル
              </button>
              <button
                onClick={handleSave}
                disabled={charInfo?.overLimit}
                className="px-4 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
